import { BriefType, CoreEntityType, LogisticsShareable, PublishInfo, ShareOSSInfo } from './share.data';
import { makeDefaultEntityTypes, Performer } from './performers.data';
import { WFWorker, WorkforceGroup } from './workforce.data';
import { JoiFile } from './files.model';
import { makeDefaultSponsorTypes, Sponsor } from './sponsors.data';
import { Exhibitor, ExhibitorStand } from './exhibitors.data';
import { Asset } from './assets.data';
import { JoiResource } from './resources.data';
import { User } from './user.data';
import { MonthFull } from '../helpers/constants';
import { Program, TimeZoneDetails } from './programs.data';
import { createId, exhibitorToStand } from '../helpers/helpers';
import { makeMapFromListOfObjectsAndKey } from '../helpers/helpers';
import { EntityBase, EntityBaseWithDeprecations } from './entity.shared.data';
import { BrandingProps } from './publish.data';
import { Page } from './pages.data';
import { Attendee } from './attendees.data';
import { JoiComment } from './tasks.data';
import { Dictionary } from "./app.data";


export const LOAD_ALL_EVENTS_FLAG = '--all events--';
export interface Label {
  id: string;
  color: string;
  label?: string; // label name
  name?: string; // color name
}

export type CustomQuestionType = 'free' | 'radio' | 'checkbox' | 'link' | 'stars' | 'text'
export const customQuestionTypes: { id: CustomQuestionType, label: string }[] = [
  { id: 'free', label: 'Free text' },
  { id: 'radio', label: 'Radio button yes/no'},
  { id: 'checkbox', label: 'Checkbox' },
  { id: 'link', label: 'Link' },
  { id: 'stars', label: 'Stars' },
  { id: 'text', label: 'Text box' }
]

export type YesNo = 'yes' | 'no' | null
export type CustomAnswer = string | boolean | YesNo | JoiFile[] | number

export interface CustomQuestion {
  id: string
  question: string
  type: CustomQuestionType
  typeIds: string[]
  created: number
  answerDefault?: CustomAnswer
  isMandatory: boolean
}

export interface EntityCall {
  id: string
  entityGroup?: string
  name?: string
  on?: boolean
  instructions?: string
}



export interface Ranking {
  name: string
  id: string
  rank: number
  cannotDelete?: boolean // for performer "standard" and maybe more future ones like this
}

export interface Venue {
  id: string
  venue: string
  googleId?: string
  address?: string 
  latitude?: number
  longitude?: number
  timeZoneId: string
}

export interface LabelObject {
  [labelId: string]: Label
}

export interface LabelMaps {
  activity_labels?: LabelObject
  asset_labels?: LabelObject
  exhibitor_labels?: LabelObject
  performer_labels?: LabelObject
  program_labels?: LabelObject
  resource_labels?: LabelObject
  session_labels?: LabelObject
  sponsor_labels?: LabelObject
  task_labels?: LabelObject
  attendee_labels?: LabelObject
}

export type LabelMap = keyof LabelMaps
export interface UpdateLabelsData {
  type: LabelMap
  template: Array<Label>
  eventId: string
}
export interface EventItem {
  id: string
  name: string
  description: string
  placeholder: string
  files: JoiFile[]
}

export interface EventInvitation {
  date_sent: {
    seconds: number
    nanoseconds: number
  } | any;
  receiver: string
  sender: string
  status: 'accepted' | 'pending' | 'declined'
}
/**
 * When you add here also add to duplicate event in functions
 */


export type EventStatus = 'Pending' | 'Confirmed' | 'Cancelled' | 'Completed' // new for budget, derived from JoiEvent properties
export interface SnapshotInfo { id: string, name: string, timestamp: number, active?: boolean }

export interface MediaTerms {
  id: string
  use: boolean
  name: string
  url: string
  typeIds: string[]
}


export interface DeprecatedEvent {
  useExhibitorMediaTerms?: boolean;
  useResourceMediaTerms?: boolean;
  useMediaTerms?: boolean;
  mediaTermsExhibitorName?: string
  mediaTermsResourceName?:string
  mediaTermsName?: string
  mediaTermsExhibitorURL?: string
  mediaTermsResourceURL?: string
  mediaTermsURL?: string
}

export interface Cue {
  id: string
  name: string
}

export interface BaseJoiPage {
  assets?: { [id: string]: Asset } 
  exhibitors?: { [id: string]: Exhibitor }
  exhibitorStands?: { [id: string]: ExhibitorStand }
  performers?: { [id: string]: Performer },
  resources?: { [id: string]: JoiResource }
  sponsors?: { [id: string]: Sponsor }
  attendees?: { [id: string]: Attendee }
  messages?: { [id: string]: JoiComment }
}

type BaseJoiPageKeys = Extract<keyof BaseJoiPage, string>;
// Create the list of keys as an array of strings
export const baseJoiPageKeys: BaseJoiPageKeys[] = [
  "assets",
  "exhibitors",
  "exhibitorStands",
  "performers",
  "resources",
  "sponsors",
  "attendees",
  "messages"
];

export interface JoiDataPage extends BaseJoiPage, Page {
  eventId: string
  accountId: string
}

export const createNewEventPageGenerator = (eventId: string, accountId: string) => (page: number): JoiDataPage => {
 const basePage: Partial<JoiDataPage> = { eventId, accountId, page, id: null }
 baseJoiPageKeys.forEach(k => basePage[k] = {})
 return basePage as JoiDataPage
}

export const standardizeJoiPage = (joiPage: JoiDataPage): void => {
  baseJoiPageKeys.forEach(k => {
    if (!joiPage[k]) joiPage[k] = {}
  })
}

export const cleanPages = <T extends Partial<JoiEvent>>(event: T) => {
  baseJoiPageKeys.forEach(k => event[k] = {})
}

export const combineEventAndPages = (event: JoiEvent, pages: JoiDataPage[]): JoiEvent => { // mutates in place and returns event
  if (!event) return null
  cleanPages(event)  
  if (!pages?.length) return event
  pages.map(standardizeJoiPage)

  // console.log({ account, clipboardPages })
  baseJoiPageKeys.forEach(k => {
    pages.map(p => Object.keys(p[k]).forEach(id => {
      event[k][id] = p[k][id]
    }))
    event[k as any] = { ...event[k] }
  })
  return event
}

export interface JoiBaseEvent extends BrandingProps { // with who can update
  accountId: string; //
  activity_labels?: Array<Label>; // program, logisticts
  asset_labels?: Array<Label>
  attendee_labels?: Array<Label>;
  byteSize?: number // for internal use only
  can_access?: boolean;
  closeReason?: 'cancelled'; // can ONLY be cancelled
  closed?: boolean; //
  closedAt?: any; //
  closedBy?: string; //
  created?: any; //
  dates?: Array<string>; // program, logisticts
  dates_string?: Array<string>; // program, logisticts
  eventId: string; //
  event_owner?:WFWorker;
  exhibitor_labels?: Array<Label>;
  first_date?: string; // program, logisticts
  icsDownload?: {[uid:string]:string}; // @depreciated
  invitation?: EventInvitation[]; // program, logisticts
  invite_sender?: WFWorker;
  is_invited?: boolean;
  is_readonly?: boolean;
  is_suspended?: boolean;
  labels?: string[]; // program
  owner_uid?:string //
  performer_labels?: Array<Label>;

  profile_codes?: string[]; // this is needed in the db for searchability - depricated
  programNames?: { [id: string]: string }
  program_labels?: Array<Label>; // program, logisticts
  publishIdsToShowInExhibitorBrief?: string[]
  publishIdsToShowInPerformerBrief?: string[]
  publishIdsToShowInResourceBrief?: string[]
  publishIdsToShowInAttendeeBrief?: string[]

  showShareInEBrief?: boolean
  showShareInPBrief?: boolean
  showShareInRBrief?: boolean
  showShareInABrief?: boolean
  eShareLogIds?: string[]
  pShareLogIds?: string[]
  rShareLogIds?: string[]
  aShareLogIds?: string[]

  showLogisticsInEBrief?: boolean
  showLogisticsInPBrief?: boolean
  showLogisticsInRBrief?: boolean
  showLogisticsInABrief?: boolean
  eBriefLogIds?: string[]
  pBriefLogIds?: string[]
  rBriefLogIds?: string[]
  aBriefLogIds?: string[]

  publishInfo?: Dictionary<PublishInfo>
  publish_codes?: string[]; // this is needed in the db for searchability - depricated
  resource_labels?: Array<Label>

  session_labels?: Array<Label>; // program
  shareInfo?: {[key:string]:ShareOSSInfo};
  showPublishedProgramsInExhibitorBrief?: boolean
  showPublishedProgramsInPerformerBrief?: boolean
  showPublishedProgramsInResourceBrief?: boolean
  showPublishedProgramsInAttendeeBrief?: boolean
  snapshots?: SnapshotInfo[]
  sponsorTypes?: Ranking[]
  performerTypes?: Ranking[]
  exhibitorTypes?: Ranking[]
  resourceTypes?: Ranking[]
  attendeeTypes?: Ranking[]

  sponsor_labels?: Array<Label>

  standTypes?: Ranking[];
  eventStatus?: EventStatus
  task_labels?: Array<Label>; // program, logisticts
  title?: string; // program
  updatedAt?:any; // all
  
  users?: Array<string>; // program, logisticts
  venues: {[key:string]:Venue}
  wf_groups?: WorkforceGroup[]; // program, logisticts
  workforce?: WFWorker[]; // program, logisticts
  percentUsed?: number
  budgetWorkforceEmails?: string[]

  attendeeQuestions: CustomQuestion[]
  performerQuestions: CustomQuestion[]
  exhibitorQuestions: CustomQuestion[]
  resourceQuestions: CustomQuestion[]
  sponsorQuestions: CustomQuestion[] 

  submitters: { [t in CoreEntityType]: EntityCall[] }
  publishEntityKeys: Dictionary<string[]>
  logisticsEntityKeys: Dictionary<string[]>
  shareEntityKeys: Dictionary<string[]>

  mediaTerms: { [id in BriefType]: MediaTerms[] }
  shareTemplateId: string

  programOrder?: string[]
  cues?: Ranking[]
  performerRoles?: Ranking[]
  shareEventInformation?: boolean
  linksInEventInformation?: boolean
  assetCategories?: Ranking[]
  messageCutoffs: Dictionary<number>
}

export interface PostMessageData {
  eventId: string
  message: JoiComment
}

export interface UpdateMessageCutoffData {
  eventId: string
  cutoff: number
}

export interface JoiEvent extends JoiBaseEvent, BaseJoiPage {}

export interface JoiEventWithDeprecatedProps extends JoiBaseEvent, DeprecatedEvent {}
export interface JoiFullEventWithDeprecatedProps extends JoiBaseEvent, BaseJoiPage, DeprecatedEvent {}

export const DBEventPropertyWhiteList: Array<keyof JoiEventWithDeprecatedProps> = [ // has to keep deprecated props because otherwise the data will be lost before being saved in the new format
  'messageCutoffs',
  'aBriefLogIds',
  'aShareLogIds',
  'attendeeTypes',
  'attendeeQuestions',
  'linksInEventInformation',
  'assetCategories',
  'shareEntityKeys',
  'showShareInEBrief',
  'showShareInPBrief',
  'showShareInRBrief',
  'showShareInABrief',
  'eShareLogIds',
  'pShareLogIds',
  'rShareLogIds',
  'shareEventInformation',
  'performerRoles',
  'cues',
  'programOrder',
  'ignoredForms',
  'templateId',
  'headerId',
  'footerId',
  'linksId',
  'socialId',
  'footerLinksId',
  'mediaTerms',
  'publishEntityKeys',
  'submitters',
  'performerQuestions',
  'exhibitorQuestions',
  'resourceQuestions',
  'sponsorQuestions',
  'useExhibitorMediaTerms',
  'useResourceMediaTerms',
  'mediaTermsExhibitorName',
  'mediaTermsResourceName',
  'mediaTermsExhibitorURL',
  'mediaTermsResourceURL',
  'accountId',
  'eventId',
  'owner_uid',
  'event_owner',
  'closed',
  'closedBy',
  'closedAt',
  'closeReason',
  'title',
  'users',
  'invitation',
  'workforce',
  'wf_groups',
  'labels',
  'first_date',
  'dates',
  'created',
  'updatedAt',
  'snapshots',
  'dates_string',
  'activity_labels',
  'attendee_labels',
  'asset_labels',
  'program_labels',
  'session_labels',
  'performer_labels',
  'sponsor_labels',
  'exhibitor_labels',
  'task_labels',
  'resource_labels',
  'useMediaTerms',
  'mediaTermsURL',
  'mediaTermsName',
  'sponsorTypes',
  'venues',
  'standTypes',
  'programNames',
  'showPublishedProgramsInPerformerBrief',
  'publishIdsToShowInPerformerBrief',
  'showPublishedProgramsInExhibitorBrief',
  'publishIdsToShowInExhibitorBrief',
  'publishIdsToShowInResourceBrief',
  'publishIdsToShowInAttendeeBrief',
  'showPublishedProgramsInAttendeeBrief',
  'showPublishedProgramsInResourceBrief',
  'budgetWorkforceEmails',
  'eventStatus',
  'performerTypes',
  'exhibitorTypes',
  'resourceTypes',
  'shareTemplateId',
  'showLogisticsInEBrief',
  'showLogisticsInPBrief',
  'showLogisticsInRBrief',
  'showLogisticsInABrief',
  'eBriefLogIds',
  'pBriefLogIds',
  'rBriefLogIds',
  'logisticsEntityKeys',
]


export const getEventOwnerId = (event: JoiEvent) => {
  let ownerId
  if (event.event_owner && event.event_owner.uid) {
    ownerId = event.event_owner.uid
  } else if (event.owner_uid) {
    ownerId = event.owner_uid 
  } 
  return ownerId
}

export interface CreateEventData {
  eventName: string
  accountId: string

  // new event only data
  firstProgram?: Partial<Program>
  defaultTimezoneDetails?: TimeZoneDetails
  
  // duplicate only data
  sourceEventId?: string
  date_diff?: number
}

export const extractNewEventPages = (event: JoiEvent, createNewPage: (page: number) => JoiDataPage) => {
  // for use in duplicate event and restore backup only.  In either case, a fresh set of pages is really the only way to go
  // just like in other cases where new pages are made, there are no ids (which match the path of the document) on them to start
  const pages: JoiDataPage[] = []
  let index = 0
  let space = 420000
  let page: JoiDataPage = createNewPage(index)
  pages.push(page)

  baseJoiPageKeys.forEach(k => {
    if(event[k]){
      const keys = Object.keys(event[k])
      keys.forEach(id => {
        try{
          const entity = event[k][id]
          const entitySize = JSON.stringify(entity).length
          if (entitySize > space) {
            index += 1
            space = 420000
            page = createNewPage(index)
            pages.push(page)
          }
          space -= entitySize
          if (!!entity) {
            page[k][id] = entity
          }
        } catch(error){
          console.error({msg: error['message'], k, id})
        }
      })
    }
  })

  cleanPages(event) // last step, removes the pages data from the event 
  return pages
}

export const createEventFoundation = (eventId: string, accountId: string, eventName: string, user: User): JoiEvent => {
  console.log('createEventFoundation', { eventId, accountId, eventName, user })
  const event = {} as JoiEvent;
  const time = new Date().getTime()
  const seconds = Math.floor(time/1000)
  event.accountId = accountId;
  event.title = eventName
  event.closed = false;
  event.eventId = eventId
  event.users = [user.uid];
  event.invitation = [];
  event.dates = [];
  event.eventStatus = 'Pending'
  event.created = {
    uid: user.uid,
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    timestamp: { seconds, nanoseconds: Math.round(1000*time%seconds) },
  };
  event.event_owner = {
      uid: user.uid
  };
  event.wf_groups = [];
  event.workforce = [{
      'email': user.email || '',
      'firstName': user.firstName || '',
      'lastName': user.lastName || '',
      'photoURL': user.photoURL || '',
      'uid': user.uid,
      'role': '',
      'status': 'accepted'
  }];
  event.programNames = {}
  console.log('createEventFoundation', { event })
  return event
}

export const getEventDatesData = (event:JoiEvent, dates:string[]): Partial<JoiEvent> => {
  if (!event) return null
  if(!event.dates
    || (event.dates.filter(d=>!dates.includes(d)).length === 0 && dates.filter(d=>!event.dates.includes(d)).length === 0)
  ) return null

  const first_date = dates && dates.length ? dates[0] : null; // .length was the fix needed for not updating an undefined value for first_date

  const eventDates: { year: number, month: string, day: number }[] = [];
  dates.forEach(d_str => {
      const ymd = d_str.split('/');
      eventDates.push({ year: +ymd[0], month: MonthFull[+ymd[1] - 1], day: +ymd[2] });
  });

  let yy = 0;
  let mm = '';
  let dd = -1;
  let arr: (number | string)[] = [];
  eventDates.reverse().forEach(d => {
      if (d.year !== yy) {
          arr = [d.day, ` ${d.month}`, ` ${d.year}`, (arr.length ? ', ' : ''), ...arr];
          yy = d.year;
          mm = d.month;
          dd = d.day;
      } else if (d.month !== mm) {
          arr = [d.day, ` ${d.month},`, ...arr];
          mm = d.month;
          dd = d.day;
      } else if (d.day !== dd) {
          if (arr[1] === ' - ') {
              if (dd - d.day === 1) {
                  arr[0] = d.day;
              } else {
                  arr = [d.day, ', ', ...arr];
              }
          } else {
              if (dd - d.day === 1) {
                  arr = [d.day, ' - ', ...arr];
              } else {
                  arr = [d.day, ', ', ...arr];
              }
          }
          dd = d.day;
      }
  });
  const dates_string = arr.map(a => '' + a) // convert back to strings
  return { dates, dates_string, first_date }
}

export const createMediaTerms = (name?: string, url?: string, use?: boolean, id = createId()): MediaTerms => ({
  id,
  name: name || 'Terms',
  url: url || '',
  use: use || false,
  typeIds: []
})

export const standardizeEvent = (event: JoiFullEventWithDeprecatedProps): void => { // mutates in place
  
  if (!event.labels) event.labels = []
  if (!event.hasOwnProperty('mediaTermsName')) event.mediaTermsName = 'Terms'
  if (!event.hasOwnProperty('mediaTermsExhibitorName')) event.mediaTermsExhibitorName = 'Terms'
  if (!event.hasOwnProperty('mediaTermsResourceName')) event.mediaTermsResourceName = 'Terms'
  
  if (!event.performers) event.performers = {}
  if (!event.sponsors) event.sponsors = {}
  if (!event.sponsorTypes) event.sponsorTypes = makeDefaultSponsorTypes()
  if (!event.performerTypes) event.performerTypes = makeDefaultEntityTypes()
  if (!event.exhibitorTypes) event.exhibitorTypes = makeDefaultEntityTypes()
  if (!event.resourceTypes) event.resourceTypes = makeDefaultEntityTypes()
  if (!event.attendeeTypes) event.attendeeTypes = makeDefaultEntityTypes()

  if (!event.standTypes) event.standTypes = []
  if (!event.exhibitors) event.exhibitors = {}
  if (!event.resources) event.resources = {}
  if (!event.attendees) event.attendees = {}

  if (event.resources["undefined"]) delete event.resources["undefined"] // this will get rid of a resource or two that was added accidentally without the base added

  if (!event.standTypes) event.standTypes = []
  if (!event.venues) event.venues = {}
  if (!event.programNames) event.programNames = {}
  if (!event.publishIdsToShowInPerformerBrief) event.publishIdsToShowInPerformerBrief = []  
  if (!event.publishIdsToShowInExhibitorBrief) event.publishIdsToShowInExhibitorBrief = []
  if (!event.publishIdsToShowInResourceBrief) event.publishIdsToShowInResourceBrief = []
  if (!event.publishIdsToShowInAttendeeBrief) event.publishIdsToShowInAttendeeBrief = []
  
  if (!event.pBriefLogIds) event.pBriefLogIds = []
  if (!event.eBriefLogIds) event.eBriefLogIds = []
  if (!event.rBriefLogIds) event.rBriefLogIds = []
  if (!event.aBriefLogIds) event.aBriefLogIds = []
  if (!event.pShareLogIds) event.pShareLogIds = []
  if (!event.eShareLogIds) event.eShareLogIds = []
  if (!event.rShareLogIds) event.rShareLogIds = []
  if (!event.aShareLogIds) event.aShareLogIds = []

  if (!event.cues) event.cues = []
  if (!event.assetCategories) event.assetCategories = []
  if (!event.performerRoles) event.performerRoles = []  

  if (!event.budgetWorkforceEmails) event.budgetWorkforceEmails = []
  if (!event.assets) event.assets = {}
  if (!event.exhibitorStands) { // this is a multistage transform, so that a bunch of empty stands cannot be created
    event.exhibitorStands = makeMapFromListOfObjectsAndKey(
      Object.values(event.exhibitors).map(exhibitorToStand).filter(s => s),
      'id'
    )
  }
  Object.values(event.performers).forEach(e => {
    if (!e.entityTypeId || !event.performerTypes.find(r => r.id === e.entityTypeId)) e.entityTypeId = 'default'
    if (!e.customQuestions) e.customQuestions = {}
  })
  Object.values(event.sponsors).forEach(e => {
    if (!e.customQuestions) e.customQuestions = {}
  })
  Object.values(event.exhibitors).forEach(e => {
    if (!e.entityTypeId || !event.exhibitorTypes.find(r => r.id === e.entityTypeId)) e.entityTypeId = 'default'
    if (!e.customQuestions) e.customQuestions = {}
  })
  Object.values(event.resources).forEach(e => {
    if (!e.entityTypeId || !event.resourceTypes.find(r => r.id === e.entityTypeId)) e.entityTypeId = 'default'
    if (!e.customQuestions) e.customQuestions = {}
  })
  Object.values(event.attendees).forEach(e => {
    if (!e.entityTypeId || !event.attendeeTypes.find(r => r.id === e.entityTypeId)) e.entityTypeId = 'default'
    if (!e.customQuestions) e.customQuestions = {}
  })

  if (!event.attendeeQuestions) event.attendeeQuestions = []
  if (!event.performerQuestions) event.performerQuestions = []
  if (!event.resourceQuestions) event.resourceQuestions = []
  if (!event.exhibitorQuestions) event.exhibitorQuestions = []
  if (!event.sponsorQuestions) event.sponsorQuestions = []
  if (!event.publishEntityKeys) event.publishEntityKeys = {}
  if (!event.logisticsEntityKeys) event.logisticsEntityKeys = {}
  if (!event.shareEntityKeys) event.shareEntityKeys = {}
  if (!event.submitters) event.submitters = {} as any
  if (!event.messageCutoffs) event.messageCutoffs = {}

  delete event.publish_codes
  delete event.publishInfo

  // console.log({ eventOwner: jsonCopyObject(event.event_owner ), event: jsonCopyObject(event) })
  if (event && event.wf_groups) event.wf_groups.filter(g => !g.realName).forEach(g => g.realName = g.name)

  if (!event.mediaTerms) {
    const { mediaTermsURL, mediaTermsName, useMediaTerms,
            mediaTermsExhibitorURL, mediaTermsExhibitorName, useExhibitorMediaTerms,
            mediaTermsResourceURL, mediaTermsResourceName, useResourceMediaTerms } = event
    event.mediaTerms = {
      performer: [createMediaTerms(mediaTermsName, mediaTermsURL, useMediaTerms, 'default')],
      exhibitor: [createMediaTerms(mediaTermsExhibitorName, mediaTermsExhibitorURL, useExhibitorMediaTerms, 'default')],
      resource: [createMediaTerms(mediaTermsResourceName, mediaTermsResourceURL, useResourceMediaTerms, 'default')],
      attendee: [createMediaTerms('Terms', '', false, 'default')],
    };
    // console.log({ mediaTerms: event.mediaTerms })
  } // don't delete the old props since this doesn't all get saved
  if (!event.mediaTerms.attendee) event.mediaTerms.attendee = [createMediaTerms('Terms', '', false, 'default')];

  [event.exhibitors, event.performers, event.resources, event.attendees].forEach(entities => {
    const typedEntities = Object.values(entities) as EntityBaseWithDeprecations[]
    typedEntities.filter(e => !e.mediaTermsMap).forEach(e => e.mediaTermsMap = { 'default': e.mediaTerms || 'Not Accepted' })
    typedEntities.filter(e => !e.noTermsMap).forEach(e => e.noTermsMap = { 'default': !!e.noMediaTermsRequired })
  }
  ) // has to be here as updates to event.mediaTerms and entities are separate.  Has to be checked for separately as well (including doing the noTermsMap and mediaTerms check each time)

  event.owner_uid = getEventOwnerId(event)
  if (event.workforce) event.event_owner = event.workforce.find(wf=>wf.uid === event.owner_uid)
  if (!event.ignoredForms) event.ignoredForms = []
  // not returning event to make clear this mutates in place
}


export const getEntity = (event: JoiEvent, id: string, entityType: CoreEntityType) => {
  if (!event || !id || !entityType) return null
  switch (entityType) {
    case 'performer':
      return event.performers[id]
    case 'exhibitor':
      return event.exhibitors[id]
    case 'resource':
      return event.resources[id]
    case 'sponsor':
      return event.sponsors[id]
    case 'attendee':
      return event.attendees[id]
    default:
      console.log(`No handling for ${entityType} getEntity`)
      return null
  }
}


export const getTypesAndQuestions = (event: JoiEvent, entityType: CoreEntityType) => {
  let types: Ranking[]
  let typesMap: Dictionary<Ranking>
  let questions: CustomQuestion[]
  let entityList: EntityBase[]
  switch (entityType) {
    case 'attendee':
      types = event.attendeeTypes
      typesMap = makeMapFromListOfObjectsAndKey(types, 'id')
      questions = event.attendeeQuestions
      entityList = Object.values(event.attendees)
      break
    case 'performer':
      types = event.performerTypes, 
      typesMap = makeMapFromListOfObjectsAndKey(event.performerTypes, 'id') 
      questions = event.performerQuestions
      entityList = Object.values(event.performers)
      break
    case 'exhibitor':
      types = event.exhibitorTypes
      typesMap = makeMapFromListOfObjectsAndKey(event.exhibitorTypes, 'id') 
      questions = event.exhibitorQuestions
      entityList = Object.values(event.exhibitors)
      break
    case 'resource':
      types = event.resourceTypes
      typesMap = makeMapFromListOfObjectsAndKey(event.resourceTypes, 'id')
      questions = event.resourceQuestions
      entityList = Object.values(event.resources)
      break
    case 'sponsor':
      types = event.sponsorTypes
      typesMap = makeMapFromListOfObjectsAndKey(event.sponsorTypes, 'id')
      questions = event.sponsorQuestions
      entityList = Object.values(event.sponsors)
      break
    default:
      console.log(`No reselector for ${entityType} types!`)
      return null
  }
  return { types, typesMap, entityList, questions }
}